
import { ComponentProps } from 'react';
import { cn } from '@/lib/utils';
import React from 'react';
import { CodeBlock } from './CodeBlock';


// Custom components for MDX elements
type MDXComponents = Record<string, React.ComponentType<Record<string, unknown>>>;

import { MDXLink } from './MDXLink';

const components: MDXComponents = {
    // Headings
    h1: ({ className, ...props }: ComponentProps<'h1'>) => (
        <h1 className={cn('text-4xl font-bold mt-10 mb-4', className)} {...props} />
    ),
    h2: ({ className, ...props }: ComponentProps<'h2'>) => (
        <h2 className={cn('text-3xl font-bold mt-8 mb-3', className)} {...props} />
    ),
    h3: ({ className, ...props }: ComponentProps<'h3'>) => (
        <h3 className={cn('text-2xl font-bold mt-6 mb-2', className)} {...props} />
    ),
    h4: ({ className, ...props }: ComponentProps<'h4'>) => (
        <h4 className={cn('text-xl font-bold mt-4 mb-2', className)} {...props} />
    ),

    // Text elements
    p: ({ className, ...props }: ComponentProps<'p'>) => (
        <p className={cn('my-4 leading-relaxed', className)} {...props} />
    ),
    a: MDXLink,

    // Lists
    ul: ({ className, ...props }: ComponentProps<'ul'>) => (
        <ul className={cn('list-disc pl-6 my-4 space-y-2', className)} {...props} />
    ),
    ol: ({ className, ...props }: ComponentProps<'ol'>) => (
        <ol className={cn('list-decimal pl-6 my-4 space-y-2', className)} {...props} />
    ),
    li: ({ className, ...props }: ComponentProps<'li'>) => (
        <li className={cn('pl-2', className)} {...props} />
    ),

    // Blockquotes
    blockquote: ({ className, ...props }: ComponentProps<'blockquote'>) => (
        <blockquote
            className={cn(
                'border-l-4 border-primary pl-4 py-2 my-4 text-muted-foreground italic',
                className
            )}
            {...props}
        />
    ),

    // Code blocks
    pre: ({ children }: ComponentProps<'pre'>) => <>{children}</>,
    code: CodeBlock,

    // Horizontal rule
    hr: ({ className, ...props }: ComponentProps<'hr'>) => (
        <hr className={cn('my-8 border-t border-border', className)} {...props} />
    ),

    // Images
    img: ({ className, alt, title, ...props }: ComponentProps<'img'>) => (
        <div className="my-6">
            <img
                className={cn('rounded-lg mx-auto max-w-full h-auto', className)}
                alt={alt}
                loading="lazy"
                decoding="async"
                {...props}
            />
            {title && (
                <div className="text-center text-sm text-muted-foreground mt-2">
                    {title}
                </div>
            )}
        </div>
    ),

    // Tables
    table: ({ className, ...props }: ComponentProps<'table'>) => (
        <div className="my-6 overflow-x-auto">
            <table className={cn('w-full border-collapse', className)} {...props} />
        </div>
    ),
    th: ({ className, ...props }: ComponentProps<'th'>) => (
        <th
            className={cn(
                'border border-border bg-muted/50 px-4 py-2 text-left font-medium',
                className
            )}
            {...props}
        />
    ),
    td: ({ className, ...props }: ComponentProps<'td'>) => (
        <td
            className={cn('border border-border px-4 py-2', className)}
            {...props}
        />
    ),
};

export default components;
